import React, { useState, useEffect } from 'react';
import PortalHeader from '../../components/Portal/PortalHeader';
import TestModeBanner from '../../components/TestMode/TestModeBanner';
import { useTestMode } from '../../contexts/TestModeContext';
import { useAuth } from '../../contexts/AuthContext/AuthContext';
import { hasPerm } from '../../utils/permissions';
import { sendAnnouncement } from '../../utils/emailService';
import { filterActiveStudents } from '../../utils/studentFilters';
import { fetchStudents as fetchAllStudents } from '../../services/studentService';
import '../../components/Portal/portalSurface.css';
import './Announcements.css';

const Announcements = () => {
	const { testMode } = useTestMode();
	const { userProfile } = useAuth();
	// sendAnnouncement is sendEmail-gated at the callable (admins/owners bypass).
	// Self-gate the composer so a teacher without the grant doesn't build a whole
	// roster + preview only to be denied at send time (#24) — mirrors EmailFamilies.
	const canSend = hasPerm(userProfile, 'sendEmail');
	const [students, setStudents] = useState([]);
	const [title, setTitle] = useState('');
	const [message, setMessage] = useState('');
	const [recipientType, setRecipientType] = useState('parents'); // 'parents', 'students', 'both'
	const [sending, setSending] = useState(false);
	const [result, setResult] = useState(null);
	const [previewCount, setPreviewCount] = useState(0);

	useEffect(() => {
		fetchStudents();
	}, [testMode]);

	useEffect(() => {
		// Update preview count when recipient type changes
		calculateRecipientCount();
	}, [recipientType, students]);

	const fetchStudents = async () => {
		try {
			const allStudents = await fetchAllStudents();

			// Filter to only active students based on test mode
			const activeStudents = filterActiveStudents(allStudents, testMode);
			setStudents(activeStudents);
		} catch (error) {
			console.error('Error fetching students:', error);
		}
	};

	const calculateRecipientCount = () => {
		let count = 0;
		students.forEach((student) => {
			if (recipientType === 'students' || recipientType === 'both') {
				if (student.email) count++;
			}
			if (recipientType === 'parents' || recipientType === 'both') {
				if (student.parent1?.email) count++;
				if (student.parent2?.email) count++;
			}
		});
		setPreviewCount(count);
	};

	const handleSend = async () => {
		if (!title.trim() || !message.trim()) {
			alert('Please enter both a title and message.');
			return;
		}

		if (previewCount === 0) {
			alert('No recipients found. Please check your student data.');
			return;
		}

		// Confirm before sending
		const confirmSend = window.confirm(
			`Are you sure you want to send this announcement to ${previewCount} recipient(s)?`
		);

		if (!confirmSend) return;

		setSending(true);
		setResult(null);

		try {
			const recipients = [];

			students.forEach((student) => {
				const studentName =
					student.fullName ||
					`${student.firstName} ${student.lastName}`;

				// Add student emails
				if (
					(recipientType === 'students' ||
						recipientType === 'both') &&
					student.email
				) {
					recipients.push({
						name: studentName,
						email: student.email,
					});
				}

				// Add parent emails
				if (recipientType === 'parents' || recipientType === 'both') {
					if (student.parent1?.email) {
						recipients.push({
							name: `${student.parent1.firstName} ${student.parent1.lastName} (${studentName}'s Parent)`,
							email: student.parent1.email,
						});
					}
					if (student.parent2?.email) {
						recipients.push({
							name: `${student.parent2.firstName} ${student.parent2.lastName} (${studentName}'s Parent)`,
							email: student.parent2.email,
						});
					}
				}
			});

			// Send announcement via Cloud Function
			const response = await sendAnnouncement({
				title,
				message,
				recipients,
			});

			setResult(response.results);

			// Clear form on success
			if (response.results.every((r) => r.success)) {
				setTitle('');
				setMessage('');
			}
		} catch (error) {
			console.error('Error sending announcement:', error);
			setResult([
				{
					email: 'Error',
					success: false,
					error: error.message,
				},
			]);
		} finally {
			setSending(false);
		}
	};

	const recipientLabel =
		recipientType === 'both'
			? 'students and parents'
			: recipientType === 'students'
			? 'students'
			: 'parents';

	if (!canSend) {
		return (
			<>
				<PortalHeader
					title='Send Announcements'
					subtitle='Send mass emails to students and parents'
				/>
				<div className="ps-page announcements-page">
					<div className="ps-alert ps-alert--warning">
						<i className="fa-sharp fa-solid fa-lock me-2" />
						<strong>No access.</strong> You don't have permission to send
						announcements. Ask an administrator to enable it in Staff Access.
					</div>
				</div>
			</>
		);
	}

	return (
		<>
			<PortalHeader
				title='Send Announcements'
				subtitle='Send mass emails to students and parents'
			/>
			<TestModeBanner />
			<div className="ps-page announcements-page">
				<div className="ps-card">
					<h2 className="ps-card__title mb-3">
						<i className="fa-sharp fa-solid fa-bullhorn" /> Compose e-mail announcement
					</h2>

					{!result ? (
						<>
							<div className={`ps-alert ${testMode ? 'ps-alert--warning' : 'ps-alert--info'}`}>
								<i className={`fa-sharp fa-solid ${testMode ? 'fa-flask' : 'fa-circle-info'} me-2`} />
								{testMode ? (
									<>
										<strong>Test Mode Active:</strong> This announcement will be sent to{' '}
										<strong>test {recipientLabel} ONLY</strong>.
										<br />
										<small>Real students are protected and will not receive this email.</small>
									</>
								) : (
									<>This announcement will be sent to all active {recipientLabel}.</>
								)}
							</div>

							<form onSubmit={(e) => e.preventDefault()}>
								<div className="ps-field announcements-field">
									<label className="ps-label" htmlFor="ann-to">Send to</label>
									<select
										id="ann-to"
										className="ps-select"
										value={recipientType}
										onChange={(e) => setRecipientType(e.target.value)}
										disabled={sending}>
										<option value="parents">Parents Only</option>
										<option value="students">Students Only</option>
										<option value="both">Both Students &amp; Parents</option>
									</select>
									<small className="text-muted">
										Will send to <strong>{previewCount}</strong> recipient{previewCount !== 1 ? 's' : ''}
									</small>
								</div>

								<div className="ps-field announcements-field">
									<label className="ps-label" htmlFor="ann-title">Subject / title</label>
									<input
										id="ann-title"
										type="text"
										className="ps-input"
										value={title}
										onChange={(e) => setTitle(e.target.value)}
										placeholder="e.g., Studio Closed for Holiday"
										disabled={sending}
										maxLength={100}
									/>
								</div>

								<div className="ps-field announcements-field">
									<label className="ps-label" htmlFor="ann-msg">Message</label>
									<textarea
										id="ann-msg"
										className="ps-textarea"
										rows={10}
										value={message}
										onChange={(e) => setMessage(e.target.value)}
										placeholder="Enter your announcement message here…"
										disabled={sending}
									/>
									<small className="text-muted">{message.length} characters</small>
								</div>

								<div className="announcements-actions">
									<div className="announcements-actions__badges">
										<span className="ps-badge ps-badge--neutral">
											{students.length} Active Student{students.length !== 1 ? 's' : ''}
										</span>
										<span className="ps-badge ps-badge--info">
											{previewCount} Email{previewCount !== 1 ? 's' : ''} Will Be Sent
										</span>
									</div>
									<button
										type="button"
										className="btn-cyv-teal"
										onClick={handleSend}
										disabled={sending || !title.trim() || !message.trim() || previewCount === 0}>
										{sending ? (
											<>
												<span className="ps-spinner announcements-btn-spinner" /> Sending…
											</>
										) : (
											<>
												<i className="fa-sharp fa-solid fa-paper-plane" /> Send announcement
											</>
										)}
									</button>
								</div>
							</form>
						</>
					) : (
						<>
							<div className={`ps-alert ${result.every((r) => r.success) ? 'ps-alert--info' : 'ps-alert--warning'}`}>
								<strong>
									{result.every((r) => r.success) ? (
										<><i className="fa-sharp fa-solid fa-circle-check me-2" /> Announcement Sent Successfully!</>
									) : (
										<><i className="fa-sharp fa-solid fa-triangle-exclamation me-2" /> Announcement Sent with Some Errors</>
									)}
								</strong>
								<hr />
								<div className="announcements-result">
									{result.filter((r) => r.success).length > 0 && (
										<div className="mb-3">
											<strong style={{ color: 'var(--cyv-teal-800)' }}>
												<i className="fa-sharp fa-solid fa-check me-1" />
												Successfully sent to {result.filter((r) => r.success).length} recipient(s):
											</strong>
											<div className="small mt-2">
												{result.filter((r) => r.success).map((r, idx) => (
													<div key={idx}>• {r.email}</div>
												))}
											</div>
										</div>
									)}
									{result.filter((r) => !r.success).length > 0 && (
										<div>
											<strong style={{ color: '#b23b3b' }}>
												<i className="fa-sharp fa-solid fa-xmark me-1" />
												Failed to send to {result.filter((r) => !r.success).length} recipient(s):
											</strong>
											<div className="small mt-2">
												{result.filter((r) => !r.success).map((r, idx) => (
													<div key={idx}>• {r.email}: {r.error}</div>
												))}
											</div>
										</div>
									)}
								</div>
							</div>
							<button
								type="button"
								className="btn-cyv-teal mt-3"
								onClick={() => {
									setResult(null);
									setTitle('');
									setMessage('');
								}}>
								Send another announcement
							</button>
						</>
					)}
				</div>

				{/* Quick Tips Card */}
				<div className="ps-card mt-4">
					<h3 className="ps-card__title mb-3">
						<i className="fa-sharp fa-solid fa-lightbulb" /> Tips for effective announcements
					</h3>
					<ul className="announcements-tips">
						<li>Keep the subject line clear and concise</li>
						<li>Use a friendly, professional tone</li>
						<li>Include specific dates and times for events</li>
						<li>Add action items or next steps if applicable</li>
						<li>Proofread before sending — you cannot unsend!</li>
					</ul>
				</div>
			</div>
		</>
	);
};

export default Announcements;
